import { useEffect, useState } from "react";
import { CheckCircle, WarningCircle, XCircle, Download } from "@phosphor-icons/react";
import api from "@/lib/api";
import { useProjects } from "@/context/ProjectContext";
import PageHeader from "@/components/PageHeader";
import { fmtNum } from "@/lib/format";

const STATUS_COLORS = {
  Compliant: { color: "text-eco", icon: CheckCircle, bg: "bg-eco/10 border-eco/30" },
  "At Risk": { color: "text-warn", icon: WarningCircle, bg: "bg-warn/10 border-warn/30" },
  "Non-Compliant": { color: "text-risk", icon: XCircle, bg: "bg-risk/10 border-risk/30" },
};

export default function Compliance() {
  const { active } = useProjects();
  const [frameworks, setFrameworks] = useState([]);
  const [selected, setSelected] = useState("EN15978");
  const [report, setReport] = useState(null);

  useEffect(() => {
    api.get("/frameworks").then((r) => setFrameworks(r.data.items));
  }, []);

  useEffect(() => {
    if (!active || !selected) return;
    api.get(`/projects/${active.id}/compliance/${selected}`).then((r) => setReport(r.data));
  }, [active, selected]);

  if (!active) return <div className="p-12 theme-ink-muted">Select a project first.</div>;

  const status = report?.status || "Compliant";
  const cfg = STATUS_COLORS[status] || STATUS_COLORS.Compliant;
  const Icon = cfg.icon;

  const downloadReport = () => {
    const blob = new Blob([JSON.stringify(report, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `compliance-${selected}-${active.id}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const downloadDoc = async (kind) => {
    const token = localStorage.getItem("ciq_token");
    const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/projects/${active.id}/compliance/${selected}/${kind}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) return;
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `compliance-${selected}-${active.id.slice(0,8)}.${kind}`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="animate-fade-up">
      <PageHeader guideId="compliance"
        overline="Module · Compliance"
        title="Framework Reports"
        subtitle="Generate alignment reports against global standards, rating systems, and ESG frameworks."
        action={
          <div className="flex items-center gap-2">
            <button
              data-testid="download-pdf-btn"
              onClick={() => downloadDoc("pdf")}
              className="flex items-center gap-2 bg-gold text-black px-4 py-2 rounded-sm text-sm font-medium hover:bg-[#e0bc40]"
            >
              <Download size={14} weight="bold" /> PDF
            </button>
            <button
              data-testid="download-docx-btn"
              onClick={() => downloadDoc("docx")}
              className="flex items-center gap-2 border theme-border px-4 py-2 rounded-sm text-sm hover:border-gold/40"
            >
              <Download size={14} /> DOCX
            </button>
            <button
              data-testid="download-report-btn"
              onClick={downloadReport}
              className="flex items-center gap-2 border theme-border px-4 py-2 rounded-sm text-sm hover:border-gold/40"
            >
              <Download size={14} /> JSON
            </button>
          </div>
        }
      />

      <section className="px-8 pt-8 grid grid-cols-1 xl:grid-cols-4 gap-4">
        <div className="border border theme-border theme-surface p-4 space-y-1 max-h-[640px] overflow-y-auto">
          <div className="overline mb-2 px-2">Frameworks</div>
          {frameworks.map((f) => (
            <button
              key={f.code}
              data-testid={`framework-${f.code}`}
              onClick={() => setSelected(f.code)}
              className={`w-full text-left px-3 py-2 border rounded-sm transition-colors ${
                selected === f.code ? "border-gold bg-gold/10" : "border theme-border hover:border-white/30"
              }`}
            >
              <div className="text-sm theme-ink">{f.code}</div>
              <div className="text-[11px] theme-ink-dim mt-0.5">{f.category} · {f.scope}</div>
            </button>
          ))}
        </div>

        <div className="xl:col-span-3 border border theme-border theme-surface p-8 space-y-6">
          {report && (
            <>
              <div className="flex items-start justify-between">
                <div>
                  <div className="overline">{report.framework.category} · {report.framework.scope}</div>
                  <h2 className="font-serif-display text-3xl mt-1">{report.framework.name}</h2>
                  <div className="text-xs theme-ink-muted mt-1 font-mono-data">Generated {report.generated_at.slice(0, 10)}</div>
                </div>
                <div className={`flex items-center gap-2 px-3 py-2 border rounded-sm ${cfg.bg}`}>
                  <Icon size={18} weight="fill" className={cfg.color} />
                  <span className={`${cfg.color} text-sm`}>{report.status}</span>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div className="border border theme-border p-4">
                  <div className="overline">Intensity</div>
                  <div className="font-mono-data text-2xl text-gold mt-2">{fmtNum(report.intensity_kg_per_m2, 1)}</div>
                  <div className="text-[11px] theme-ink-dim">kgCO₂e/m²</div>
                </div>
                <div className="border border theme-border p-4">
                  <div className="overline">Target</div>
                  <div className="font-mono-data text-2xl theme-ink mt-2">{fmtNum(report.target_kg_per_m2)}</div>
                  <div className="text-[11px] theme-ink-dim">kgCO₂e/m²</div>
                </div>
                <div className="border border theme-border p-4">
                  <div className="overline">Embodied</div>
                  <div className="font-mono-data text-2xl theme-ink mt-2">{fmtNum(report.embodied_kg / 1000, 0)}</div>
                  <div className="text-[11px] theme-ink-dim">tCO₂e</div>
                </div>
              </div>

              <div className="space-y-4">
                {report.sections.map((s, i) => (
                  <div key={i} className="border-l-2 border-gold pl-4 py-1">
                    <div className="overline">{s.title}</div>
                    <p className="text-sm theme-ink-muted leading-relaxed mt-1">{s.content}</p>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </section>
      <div className="pb-12" />
    </div>
  );
}
